/**
 * B1080 — Aperçu du courriel avant envoi (Eric 2026-09-05 SPEC STRICK PROD).
 *
 * Partagé par le rapport forfait AI et le rapport de fiabilité : valider les
 * chiffres après l'envoi n'a plus d'intérêt. L'aperçu montre le courriel
 * EXACT (même fonction de rendu que l'envoi), avec les destinataires cochés
 * et la date du prochain envoi automatique au-dessus.
 *
 * Tâche 1431 (Eric 2026-10-01) — Cette fenêtre était restée entièrement en
 * français, écrite sans `useLang` : l'anglais s'arrêtait donc au bouton qui
 * l'ouvre. Comme pour l'aperçu du courriel du lundi (1430), on distingue ce
 * que l'INTERFACE dit — traduit — du COURRIEL lui-même, qui reste en français
 * parce que c'est le message réel qui partira.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Eye, X } from "@phosphor-icons/react";
import { useLang } from "../../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

export const ReportPreviewModal = ({ endpoint, testid, label }) => {
  const { lang, tr } = useLang();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}${endpoint}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || tr("Aperçu impossible", "Preview unavailable"));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [endpoint, tr]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const fmt = (iso) =>
    iso
      ? `${new Date(iso).toLocaleString(lang === "en" ? "en-CA" : "fr-CA", {
          dateStyle: "long",
          timeStyle: "short",
          timeZone: "America/Toronto",
        })} ${tr("(heure de Montréal)", "(Montreal time)")}`
      : tr("aucun envoi prévu", "no send scheduled");

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-300 text-sm text-zinc-700 hover:bg-zinc-50"
        data-testid={`${testid}-open`}
      >
        <Eye size={16} />
        {label || tr("Aperçu du courriel", "Email preview")}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto"
          data-testid={`${testid}-modal`}
        >
          <div className="w-full max-w-3xl my-8 rounded-xl bg-white shadow-xl">
            <div className="flex items-start justify-between gap-4 border-b border-zinc-200 px-5 py-4">
              <div>
                <h3 className="text-base font-semibold text-zinc-900">
                  {tr("Aperçu du courriel — rien n'est envoyé",
                      "Email preview — nothing is sent")}
                </h3>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {tr("Contenu identique à celui qui partira : même rendu, mêmes chiffres.",
                      "Identical to what will go out: same rendering, same figures.")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg text-zinc-500 hover:bg-zinc-100"
                data-testid={`${testid}-close`}
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-5 py-4 space-y-3">
              {loading && (
                <p className="text-sm text-zinc-500" data-testid={`${testid}-loading`}>
                  {tr("Chargement de l'aperçu…", "Loading the preview…")}
                </p>
              )}
              {error && (
                <p className="text-sm text-red-700" data-testid={`${testid}-error`}>
                  {error}
                </p>
              )}
              {data && (
                <>
                  <dl className="grid sm:grid-cols-3 gap-3 text-xs" data-testid={`${testid}-meta`}>
                    <div>
                      <dt className="font-semibold uppercase tracking-wide text-zinc-500">
                        {tr("Destinataires", "Recipients")}
                      </dt>
                      <dd className="text-zinc-800 mt-0.5" data-testid={`${testid}-recipients`}>
                        {(data.recipients || []).length
                          ? (data.recipients || []).join(", ")
                          : tr("aucun destinataire coché", "no recipient ticked")}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold uppercase tracking-wide text-zinc-500">
                        {tr("Périodicité", "Frequency")}
                      </dt>
                      <dd className="text-zinc-800 mt-0.5">
                        {(lang === "en" ? (data.frequency_label_en || data.frequency_label) : data.frequency_label)
                          || data.frequency} ·{" "}
                        {lang === "fr"
                          ? `${String(data.send_hour ?? 8).padStart(2, "0")} h, heure de Montréal`
                          : `${String(data.send_hour ?? 8).padStart(2, "0")}:00, Montreal time`}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold uppercase tracking-wide text-zinc-500">
                        {tr("Prochain envoi automatique", "Next automatic send")}
                      </dt>
                      <dd className="text-zinc-800 mt-0.5" data-testid={`${testid}-next-send`}>
                        {fmt(data.next_send_at)}
                      </dd>
                    </div>
                  </dl>

                  <div className="rounded-lg border border-zinc-200 px-3 py-2 text-sm text-zinc-800">
                    <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                      {tr("Objet", "Subject")}
                    </span>
                    <div data-testid={`${testid}-subject`}>{data.subject}</div>
                  </div>

                  {/* Le courriel réel part en français : on le dit, pour qu'un
                      lecteur anglophone ne prenne pas ce français pour une
                      traduction oubliée (même choix qu'en 1430). */}
                  {lang !== "fr" && (
                    <p className="text-[11px] text-zinc-500" data-testid={`${testid}-lang-note`}>
                      The email below is shown exactly as it will be sent — it goes out in French.
                    </p>
                  )}

                  <div
                    className="rounded-lg border border-zinc-200 bg-white p-4 max-h-[55vh] overflow-y-auto"
                    data-testid={`${testid}-html`}
                    dangerouslySetInnerHTML={{ __html: data.html || "" }}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportPreviewModal;
